import type { ResumeData, ResumeSettings } from '../types/resume';
import { Mail, Phone, MapPin, Globe, Linkedin } from 'lucide-react';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface Props {
  data: ResumeData;
  settings?: ResumeSettings;
}

function renderDescription(desc: string, lineSpacing: number, bScale: number) {
  if (!desc) return null;
  const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const style = { fontSize: `${0.875 * bScale}rem`, lineHeight: lineSpacing };
  if (lines.length <= 1) {
    return <p className="text-slate-600 whitespace-pre-line" style={style}>{desc}</p>;
  }
  return (
    <ul className="list-disc pl-4 space-y-1 text-slate-600" style={style}>
      {lines.map((line, i) => (
        <li key={i}>{line.replace(/^[-•\s]+/, '')}</li>
      ))}
    </ul>
  );
}

export function ModernTemplate({ data, settings }: Props) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  return (
    <div
      className="bg-white text-slate-800 h-full"
      style={{
        fontFamily: `${s.fonts.primary}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="bg-slate-900 text-white px-10 py-8 -mx-10 -mt-10 mb-6" style={{ textAlign: s.headerAlignment }}>
        <h1 className="font-bold tracking-tight" style={{ fontFamily: `${s.fonts.secondary}, system-ui, sans-serif`, fontSize: `${2.25 * hScale}rem` }}>
          {personal.fullName || 'Your Name'}
        </h1>
        {s.photo && (
          <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mt-3 border-2 border-white" />
        )}
        <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-300 justify-center">
          {personal.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {personal.email}</span>}
          {personal.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {personal.phone}</span>}
          {personal.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {personal.location}</span>}
          {personal.website && <span className="flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> {personal.website}</span>}
          {personal.linkedin && <span className="flex items-center gap-1"><Linkedin className="h-3.5 w-3.5" /> {personal.linkedin}</span>}
        </div>
      </header>

      <main style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.sectionGap }}>
        {personal.summary && (
          <section>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        )}

        {experience.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-widest text-primary-600 mb-3 border-b-2 border-primary-100 pb-1" style={{ fontSize: `${0.75 * hScale}rem` }}>
              WORK EXPERIENCE
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <div key={exp.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{exp.title}</h3>
                    <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="font-medium text-primary-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company} {exp.location && `• ${exp.location}`}</p>
                  {renderDescription(exp.description, s.spacing.lineSpacing, bScale)}
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-widest text-primary-600 mb-3 border-b-2 border-primary-100 pb-1" style={{ fontSize: `${0.75 * hScale}rem` }}>
              Education
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <div key={edu.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{edu.school}</h3>
                    <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>
                      {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree} {edu.field && `in ${edu.field}`}</p>
                  {edu.description && <p className="text-slate-500 mt-0.5" style={{ fontSize: `${0.75 * bScale}rem` }}>{edu.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {skills.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-widest text-primary-600 mb-3 border-b-2 border-primary-100 pb-1" style={{ fontSize: `${0.75 * hScale}rem` }}>
              Skills
            </h2>
            <div className="flex flex-wrap gap-2">
              {skills.map((skill) => (
                <span key={skill.id} className="inline-flex items-center rounded-md bg-slate-700 px-3 py-1.5 text-xs font-medium text-white" style={{ fontSize: `${0.75 * bScale}rem` }}>
                  {skill.name}
                </span>
              ))}
            </div>
          </section>
        )}

        {projects.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-widest text-primary-600 mb-3 border-b-2 border-primary-100 pb-1" style={{ fontSize: `${0.75 * hScale}rem` }}>
              Projects
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <div key={proj.id}>
                  <div className="flex justify-between items-baseline">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{proj.name}</h3>
                    {proj.url && <a href={proj.url} className="text-primary-600 hover:underline" style={{ fontSize: `${0.75 * bScale}rem` }}>Link</a>}
                  </div>
                  <p className="text-slate-600 mt-0.5" style={{ fontSize: `${0.875 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{proj.description}</p>
                  {proj.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {proj.technologies.map((t) => (
                        <span key={t} className="text-primary-700 px-1.5 py-0.5 rounded bg-primary-50" style={{ fontSize: `${0.625 * bScale}rem` }}>{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {certifications.length > 0 && (
          <section>
            <h2 className="font-bold uppercase tracking-widest text-primary-600 mb-3 border-b-2 border-primary-100 pb-1" style={{ fontSize: `${0.75 * hScale}rem` }}>
              Certifications
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {certifications.map((cert) => (
                <div key={cert.id} className="flex justify-between items-baseline">
                  <span className="font-medium text-slate-800" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-500" style={{ fontSize: `${0.75 * bScale}rem` }}>{cert.issuer} • {cert.date}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
